import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardList, Shield, Save, Loader2, MessageSquare } from "lucide-react";
import { API } from "@/lib/admin-constants";
import FormFieldsEditor from "@/components/admin/FormFieldsEditor";

const DEFAULT_CONSENT_TEXT = "By submitting this form, you agree to receive text messages from Commonwealth FC regarding registration, scheduling, and program updates. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance.";

const DEFAULT_JOIN_FIELDS = [
  { id: "name", label: "Player Name", type: "text", placeholder: "Player's full name", required: true, enabled: true, maps_to: "name", options: [] },
  { id: "parent_name", label: "Parent / Guardian Name", type: "text", placeholder: "Parent or guardian's full name", required: true, enabled: true, maps_to: "parent_name", options: [] },
  { id: "email", label: "Email Address", type: "email", placeholder: "parent@email.com", required: true, enabled: true, maps_to: "email", options: [] },
  { id: "phone", label: "Phone #", type: "tel", placeholder: "(555) 555-5555", required: true, enabled: true, maps_to: "phone", options: [] },
  { id: "player_age", label: "What age group are you interested in?", type: "select", placeholder: "Select age group", required: true, enabled: true, maps_to: "player_age", options: ["U8 (2018-2019)", "U10 (2016-2017)", "U12 (2014-2015)", "U14 (2012-2013)", "U16 (2010-2011)", "U18 (2008-2009)", "High School Girls"] },
  { id: "soccer_experience", label: "Additional Information / Soccer Experience (Optional)", type: "textarea", placeholder: "Previous clubs, years playing, positions...", required: false, enabled: true, maps_to: "soccer_experience", options: [] },
];

const DEFAULT_COACH_FIELDS = [
  { id: "name", label: "Coach / Contact Name", type: "text", placeholder: "Your full name", required: true, enabled: true, maps_to: "name", options: [] },
  { id: "organization", label: "Current Club / Organization", type: "text", placeholder: "Current club or organization name", required: false, enabled: true, maps_to: "organization", options: [] },
  { id: "email", label: "Email Address", type: "email", placeholder: "coach@email.com", required: true, enabled: true, maps_to: "email", options: [] },
  { id: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 555-5555", required: true, enabled: true, maps_to: "phone", options: [] },
  { id: "player_age", label: "Team Age Group(s)", type: "select", placeholder: "Select age group", required: true, enabled: true, maps_to: "player_age", options: ["U8-U9", "U10-U11", "U12-U13", "U14-U15", "U16-U17", "U18-U19", "Girls High School", "Multiple Age Groups"] },
  { id: "num_players", label: "Number of Players", type: "text", placeholder: "e.g., 16", required: false, enabled: true, maps_to: "num_players", options: [] },
  { id: "message", label: "Tell Us About Your Team", type: "textarea", placeholder: "What are you looking for in a club?...", required: false, enabled: true, maps_to: "message", options: [] },
];

const FormBuilder = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/site-content`, { withCredentials: true });
        setContent(res.data);
      } catch {
        toast.error("Failed to load form configuration");
      }
    };
    fetchContent();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-content`, content, { withCredentials: true });
      toast.success("Form configuration saved");
    } catch {
      toast.error("Failed to save form configuration");
    } finally {
      setSaving(false);
    }
  };

  if (!content) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-cfc-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="form-builder-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-cfc-primary">Form Builder</h1>
          <p className="text-sm text-cfc-text-muted mt-1">Configure which fields appear on your registration and inquiry forms</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-cfc-primary hover:bg-cfc-primary-hover"
          data-testid="save-forms-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
          Save All Forms
        </Button>
      </div>

      <div className="space-y-8">
        {/* Registration / Join Form */}
        <div className="bg-white border border-cfc-border rounded-lg p-6" data-testid="join-form-builder">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-cfc-border">
            <div className="w-9 h-9 rounded bg-cfc-primary/10 flex items-center justify-center">
              <ClipboardList size={18} className="text-cfc-primary" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-cfc-primary">Registration / Join Form</h2>
              <p className="text-xs text-cfc-text-muted">Fields shown on the /join page registration form</p>
            </div>
          </div>
          <FormFieldsEditor
            title="Form Fields"
            fields={content.join_page?.form_fields?.length > 0 ? content.join_page.form_fields : DEFAULT_JOIN_FIELDS}
            onChange={(fields) => setContent(prev => ({
              ...prev,
              join_page: { ...prev.join_page, form_fields: fields }
            }))}
          />
        </div>

        {/* Coach Inquiry Form */}
        <div className="bg-white border border-cfc-border rounded-lg p-6" data-testid="coach-form-builder">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-cfc-border">
            <div className="w-9 h-9 rounded bg-cfc-primary/10 flex items-center justify-center">
              <Shield size={18} className="text-cfc-primary" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-cfc-primary">Coach / Team Inquiry Form</h2>
              <p className="text-xs text-cfc-text-muted">Fields shown on the /coaches page inquiry form</p>
            </div>
          </div>
          <FormFieldsEditor
            title="Form Fields"
            fields={content.coaches_page?.form_fields?.length > 0 ? content.coaches_page.form_fields : DEFAULT_COACH_FIELDS}
            onChange={(fields) => setContent(prev => ({
              ...prev,
              coaches_page: { ...prev.coaches_page, form_fields: fields }
            }))}
          />
          <div className="mt-5 pt-5 border-t border-cfc-border">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={14} className="text-cfc-text-muted" />
              <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold">SMS/Legal Consent Text</Label>
            </div>
            <Textarea
              value={content.coaches_page?.consent_text ?? DEFAULT_CONSENT_TEXT}
              onChange={(e) => setContent(prev => ({
                ...prev,
                coaches_page: { ...prev.coaches_page, consent_text: e.target.value }
              }))}
              rows={3}
              className="text-sm"
              placeholder="By submitting this form, you agree to..."
              data-testid="coach-consent-text"
            />
            <p className="text-[11px] text-cfc-text-muted mt-1">Displayed below the submit button. Leave empty to hide.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
